import type { HeadId, StageId } from '../data/content'
import { CHAPTERS, type Beat } from '../data/script'

export type TimelineBeat = Beat & {
  chapter: number
  index: number
  start: number
  end: number
  stage: StageId
}

/** How long a sampling beat highlights its pick before the token flies in. */
export const SAMPLE_DELAY_MS = 700

/** Roughly how long a picked token takes to fly down into the blank. */
export const LAND_MS = 900

export const TIMELINE: TimelineBeat[] = (() => {
  const beats: TimelineBeat[] = []
  let start = 0
  CHAPTERS.forEach((chapter, c) => {
    chapter.beats.forEach((beat) => {
      beats.push({
        ...beat,
        chapter: c,
        index: beats.length,
        start,
        end: start + beat.ms,
        stage: beat.stage ?? chapter.stage,
      })
      start += beat.ms
    })
  })
  return beats
})()

export const TOTAL_MS = TIMELINE[TIMELINE.length - 1].end

export const CHAPTER_SPANS = CHAPTERS.map((_, c) => {
  const beats = TIMELINE.filter((b) => b.chapter === c)
  return { start: beats[0].start, end: beats[beats.length - 1].end }
})

export function beatAt(time: number): TimelineBeat {
  return TIMELINE.find((b) => time < b.end) ?? TIMELINE[TIMELINE.length - 1]
}

/** Everything the film dictates at `time`, before any of the viewer's changes. */
export function shotAt(time: number) {
  const beat = beatAt(time)
  let head: HeadId = 'meaning'
  let temperature = 1
  let blank = false
  for (const b of TIMELINE) {
    if (b.start > time) break
    if (b.head) head = b.head
    if (b.temperature !== undefined) temperature = b.temperature
    if (b.blank) blank = true
  }
  const elapsed = time - beat.start
  const samples = TIMELINE.filter((b) => b.sample && b.start + SAMPLE_DELAY_MS <= time).length
  const landed = TIMELINE.filter(
    (b) => b.sample && b.start + SAMPLE_DELAY_MS + LAND_MS <= time,
  ).length
  const picking = Boolean(beat.sample) && elapsed < SAMPLE_DELAY_MS
  return {
    beat,
    stage: beat.stage,
    head,
    temperature,
    samples,
    /** A picked token is still in the air on its way to the blank. */
    landing: samples > landed,
    picking,
    blank,
    chat: beat.chat ?? null,
    framing: beat.shot ?? null,
    elapsed,
    progress: Math.min(1, elapsed / beat.ms),
  }
}

export function formatTime(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}
